'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';

import { CONTACT_EMAIL } from '@/lib/constants';
import type { LegalContent } from '@/lib/legal';

interface LegalDocumentProps {
	code: string;
	title: string;
	intro: string;
	pdfHref: string;
	meta: {
		version: string;
		revised: string;
		effective: string;
		language: string;
	};
	content: LegalContent;
}

// Fixed header clearance for anchor scroll + scrollspy threshold.
const SCROLL_OFFSET = 150;

export function LegalDocument({
	code,
	title,
	intro,
	pdfHref,
	meta,
	content,
}: LegalDocumentProps) {
	const t = useTranslations('Legal');
	const [active, setActive] = useState(0);
	// Suppress scrollspy while a TOC click is smooth-scrolling.
	const lockRef = useRef(false);

	// Scrollspy — highlight the section currently under the header. setState lives
	// in the scroll callback (not the effect body), so no set-state-in-effect.
	useEffect(() => {
		const update = () => {
			if (lockRef.current) return;
			const secs = document.querySelectorAll<HTMLElement>('section[data-sec]');
			let cur = 0;
			secs.forEach((s) => {
				if (s.getBoundingClientRect().top <= SCROLL_OFFSET + 4) {
					cur = Number(s.dataset.sec) || 0;
				}
			});
			if (
				window.innerHeight + window.scrollY >=
				document.documentElement.scrollHeight - 4
			) {
				cur = secs.length - 1;
			}
			setActive((prev) => (prev === cur ? prev : cur));
		};
		window.addEventListener('scroll', update, { passive: true });
		update();
		return () => window.removeEventListener('scroll', update);
	}, []);

	const goTo = (anchor: string, idx: number) => {
		const el = document.getElementById(anchor);
		if (!el) return;
		lockRef.current = true;
		setActive(idx);
		const y = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
		window.scrollTo({ top: y, behavior: 'smooth' });
		window.setTimeout(() => {
			lockRef.current = false;
		}, 800);
	};

	return (
		<div className="pt-2">
			{/* Header bar */}
			<div className="border-border text-muted flex flex-wrap items-center justify-between gap-3 border-b pb-3 font-mono text-[11px] tracking-[1.5px] uppercase">
				<div>ONEKYC // {t('legal')}</div>
				<div className="flex items-center">
					<span
						className="auth-cursor bg-fg mr-2 inline-block h-[7px] w-[7px]"
						aria-hidden="true"
					/>
					DOC_TYPE: {code}
				</div>
			</div>

			{/* Breadcrumb + title */}
			<nav
				aria-label={t('legal')}
				className="text-muted mt-7 flex flex-wrap gap-2 font-mono text-[11px] tracking-[1.5px] uppercase"
			>
				<span>{t('legal')}</span>
				<span aria-hidden="true">/</span>
				<span className="text-fg">{title}</span>
			</nav>
			<h1 className="mt-3.5 text-[clamp(26px,7vw,58px)] leading-none font-normal tracking-[-0.03em] [overflow-wrap:anywhere] break-words">
				{title}
			</h1>

			<div className="text-muted mt-5 flex flex-wrap gap-x-7 gap-y-2.5 font-mono text-[11px] tracking-[1px]">
				<span>
					{t('version')}: {meta.version}
				</span>
				<span>
					{t('revised')}: {meta.revised}
				</span>
				<span>
					{t('effective')}: {meta.effective}
				</span>
				<span>
					{t('language')}: {meta.language}
				</span>
			</div>

			<p className="text-accent mt-4.5 max-w-[70ch] text-[15px] leading-[1.65]">
				{intro}
			</p>

			<div className="mt-5 flex flex-wrap gap-2.5">
				<a
					href={pdfHref}
					download
					className="bg-fg text-bg flex cursor-crosshair items-center gap-2.5 px-[18px] py-[13px] font-mono text-[11px] tracking-[1.5px] uppercase no-underline transition-opacity hover:opacity-85"
				>
					{t('downloadPdf')} <span aria-hidden="true">↓</span>
				</a>
				<button
					type="button"
					onClick={() => window.print()}
					className="border-fg text-fg hover:bg-fg hover:text-bg cursor-crosshair border bg-transparent px-[18px] py-[13px] font-mono text-[11px] tracking-[1.5px] uppercase transition-colors"
				>
					{t('print')}
				</button>
			</div>

			<div className="bg-border mt-7 h-px" />

			{/* TOC + content */}
			<div className="mt-8 grid grid-cols-1 items-start gap-11 min-[820px]:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
				<aside className="min-[820px]:sticky min-[820px]:top-[150px]">
					<div className="text-muted font-mono text-[10px] tracking-[2px] uppercase">
						{t('tableOfContents')}
					</div>
					<nav
						aria-label={t('tableOfContents')}
						className="mt-3.5 flex flex-col"
					>
						{content.sections.map((s, i) => (
							<button
								key={s.anchor}
								type="button"
								onClick={() => goTo(s.anchor, i)}
								aria-current={i === active ? 'true' : undefined}
								className={`flex w-full cursor-crosshair items-baseline gap-2.5 border-l-2 py-2.5 pl-3 text-left text-[13.5px] leading-[1.35] transition-colors ${
									i === active
										? 'border-fg text-fg font-semibold'
										: 'border-border text-muted hover:text-fg'
								}`}
							>
								<span className="text-muted shrink-0 font-mono text-[10px]">
									{s.num}
								</span>
								<span>{s.title}</span>
							</button>
						))}
					</nav>
					<div className="border-border bg-surface mt-5 border p-4">
						<div className="text-muted font-mono text-[10px] tracking-[1.5px] uppercase">
							{t('contact')}
						</div>
						<a
							href={`mailto:${CONTACT_EMAIL}`}
							className="text-accent hover:text-fg mt-2 block text-[13px] leading-[1.5] no-underline transition-colors"
						>
							{CONTACT_EMAIL}
						</a>
					</div>
				</aside>

				<div className="max-w-[760px] min-w-0 pb-[clamp(120px,38vh,360px)]">
					{content.sections.map((s, i) => (
						<section
							key={s.anchor}
							id={s.anchor}
							data-sec={i}
							className="scroll-mt-[150px] pb-10"
						>
							<div className="flex min-w-0 items-baseline gap-3.5">
								<span className="text-fg font-mono text-[13px] font-bold">
									{s.num}
								</span>
								<h2 className="min-w-0 text-[clamp(20px,2.4vw,26px)] leading-[1.2] font-medium tracking-[-0.015em] [overflow-wrap:anywhere] break-words">
									{s.title}
								</h2>
							</div>
							<div className="mt-4 min-w-0 pl-[30px]">
								{s.blocks.map((b, bi) =>
									b.type === 'list' ? (
										<div key={bi} className="mb-4 flex flex-col gap-2.5">
											{(b.items ?? []).map((it, ii) => (
												<div key={ii} className="flex items-start gap-3">
													<span
														className="text-fg shrink-0 font-mono text-[13px] leading-[1.5]"
														aria-hidden="true"
													>
														—
													</span>
													<span className="text-accent min-w-0 text-[14.5px] leading-[1.6] [overflow-wrap:anywhere] break-words">
														{it}
													</span>
												</div>
											))}
										</div>
									) : (
										<p
											key={bi}
											className="text-accent mb-3.5 text-[15px] leading-[1.7] [overflow-wrap:anywhere] break-words"
										>
											{b.text}
										</p>
									),
								)}
							</div>
							<div className="bg-border ml-[30px] h-px" />
						</section>
					))}
					<div className="text-muted font-mono text-[11px] tracking-[0.5px]">
						[ {t('endOfDocument')} · REV {meta.revised} ]
					</div>
				</div>
			</div>
		</div>
	);
}
